import React, { useState, useEffect } from 'react';
import { 
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  CurrencyDollarIcon,
  TagIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  LifebuoyIcon,
} from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import SupportTicketModal from '../components/SupportTicketModal';
import { useAuth } from '../contexts/AuthContext';
import { productService, Product, ProductFormData } from '../services/productService';
import { imageUploadService, ProductImage } from '../services/imageUploadService';
import { OrderService, Order } from '../services/orderService';
import StripeService from '../services/stripeService';
import { EmailService } from '../services/emailService';
import { formatStripeConnectSetupError } from '../utils/stripeConnectErrors';
import { shouldNotifyLowStock } from '../utils/inventoryAlerts';
import {
  PRODUCT_CATEGORIES,
  PRODUCT_TYPES,
  ProductType,
  getCategoryFormLabel,
} from '../constants/productCategories';
import {
  STRIPE_CONNECT_COUNTRIES,
  StripeConnectCountryCode,
} from '../constants/stripeConnectCountries';
import './VendorPanel.css';


const VendorPanel: React.FC = () => {
  const { user, userAccountType } = useAuth();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'add-product' | 'orders' | 'support'>('dashboard');
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [uploadedImages, setUploadedImages] = useState<ProductImage[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadProductId, setUploadProductId] = useState<string | null>(null);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [vendorEarnings, setVendorEarnings] = useState({
    totalEarnings: 0,
    pendingPayouts: 0,
    totalOrders: 0,
    totalSales: 0,
  });
  const [stripeConnectStatus, setStripeConnectStatus] = useState<{
    connected: boolean;
    onboardingComplete: boolean;
    payoutsEnabled: boolean;
    chargesEnabled: boolean;
    accountId?: string;
  }>({
    connected: false,
    onboardingComplete: false,
    payoutsEnabled: false,
    chargesEnabled: false,
  });
  const [stripeSetupError, setStripeSetupError] = useState<string | null>(null);
  const [stripeConnectCountry, setStripeConnectCountry] = useState<StripeConnectCountryCode | ''>('');
  const [stripeConnectLoading, setStripeConnectLoading] = useState(true);
  const [stripeResetting, setStripeResetting] = useState(false);

  // Product form state
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    shortDescription: '',
    currentPrice: '',
    originalPrice: '',
    currency: 'EUR',
    productType: 'physical' as ProductType,
    quantity: '',
    sku: '',
    lowStockThreshold: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    shippingClass: 'standard' as 'standard' | 'express' | 'overnight',
    freeShipping: false,
    shippingCost: '',
    tags: '',
    keywords: '',
    categoryId: '',
    specifications: {} as { [key: string]: string | number | boolean }
  });

  // Check if user is vendor
  useEffect(() => {
    if (!user || userAccountType !== 'vendor') {
      setError('Access denied. Vendor privileges required.');
      setLoading(false);
      return;
    }
  }, [user, userAccountType]);

  // Load products and orders data
  useEffect(() => {
    const loadData = async () => {
      if (!user || userAccountType !== 'vendor') {
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        const [productsData, ordersData, earningsData] = await Promise.all([
          productService.getVendorProducts(user.uid),
          OrderService.getInstance().getVendorOrders(user.uid),
          OrderService.getInstance().getVendorEarnings(user.uid),
        ]);
        
        setProducts(productsData);
        setOrders(ordersData);
        setVendorEarnings(earningsData);

        setStripeConnectLoading(true);
        try {
          const stripeStatus = await StripeService.getInstance().getVendorConnectStatus(user.uid);
          setStripeConnectStatus(stripeStatus);
        } catch (err) {
          console.warn('Stripe Connect status unavailable:', err);
        } finally {
          setStripeConnectLoading(false);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'message' in err && typeof (err as { message: unknown }).message === 'string'
              ? (err as { message: string }).message
              : 'Unknown error';
        setError(`Failed to load data: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, userAccountType]);

  useEffect(() => {
    if (!user || userAccountType !== 'vendor') return;
    const params = new URLSearchParams(window.location.search);
    const stripeParam = params.get('stripe');
    if (stripeParam !== 'return' && stripeParam !== 'refresh') return;

    setStripeConnectLoading(true);
    StripeService.getInstance()
      .getVendorConnectStatus(user.uid)
      .then(setStripeConnectStatus)
      .catch((err) => console.error('Stripe status refresh:', err))
      .finally(() => setStripeConnectLoading(false));

    params.delete('stripe');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`;
    window.history.replaceState({}, '', nextUrl);
  }, [user, userAccountType]);

  const handleStripeConnectSetup = async () => {
    if (!user?.email) return;
    if (!stripeConnectCountry) {
      setStripeSetupError('Select the country where your business is based before connecting Stripe.');
      return;
    }
    setStripeSetupError(null);
    try {
      await StripeService.getInstance().startVendorOnboarding({
        vendorId: user.uid,
        email: user.email,
        country: stripeConnectCountry,
      });
    } catch (err) {
      console.error('Stripe onboarding error:', err);
      const message = err instanceof Error ? err.message : 'Failed to start Stripe onboarding';
      setStripeSetupError(formatStripeConnectSetupError(message));
    }
  };

  const handleStripeConnectReset = async () => {
    if (!user) return;
    const confirmed = window.confirm(
      'Clear the linked Stripe account and start onboarding again? Use this if the wrong business was selected.'
    );
    if (!confirmed) return;

    setStripeSetupError(null);
    setStripeResetting(true);
    try {
      await StripeService.getInstance().resetVendorConnect();
      setStripeConnectStatus({
        connected: false,
        onboardingComplete: false,
        payoutsEnabled: false,
        chargesEnabled: false,
      });
      setStripeConnectCountry('');
    } catch (err) {
      console.error('Stripe reset error:', err);
      const message = err instanceof Error ? err.message : 'Failed to reset Stripe connection';
      setStripeSetupError(formatStripeConnectSetupError(message));
    } finally {
      setStripeResetting(false);
    }
  };

  const handleStripeDashboard = async () => {
    if (!user) return;
    setStripeSetupError(null);
    try {
      await StripeService.getInstance().openVendorDashboard(user.uid);
    } catch (err) {
      console.error('Stripe dashboard error:', err);
      const message = err instanceof Error ? err.message : 'Failed to open Stripe dashboard';
      setStripeSetupError(formatStripeConnectSetupError(message));
    }
  };

  const handleProductSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    try {
      const isDigital = productForm.productType === 'digital';
      const quantity = parseInt(productForm.quantity);
      const lowStockThreshold = parseInt(productForm.lowStockThreshold) || 0;
      const previousQuantity = editingProduct?.inventory.quantity;

      const productData: ProductFormData = {
        name: productForm.name,
        description: productForm.description,
        shortDescription: productForm.shortDescription,
        currentPrice: parseFloat(productForm.currentPrice),
        originalPrice: productForm.originalPrice ? parseFloat(productForm.originalPrice) : undefined,
        currency: productForm.currency,
        quantity,
        sku: productForm.sku,
        lowStockThreshold,
        weight: isDigital ? 0 : parseFloat(productForm.weight),
        length: isDigital ? 0 : (productForm.length ? parseFloat(productForm.length) : undefined),
        width: isDigital ? 0 : (productForm.width ? parseFloat(productForm.width) : undefined),
        height: isDigital ? 0 : (productForm.height ? parseFloat(productForm.height) : undefined),
        shippingClass: productForm.shippingClass,
        freeShipping: isDigital ? true : productForm.freeShipping,
        shippingCost: isDigital ? 0 : (parseFloat(productForm.shippingCost) || 0),
        tags: productForm.tags ? productForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        keywords: productForm.keywords ? productForm.keywords.split(',').map(keyword => keyword.trim()).filter(keyword => keyword) : [],
        categoryId: productForm.categoryId,
        specifications: {
          ...productForm.specifications,
          productType: productForm.productType,
        },
        images: uploadedImages.reduce((acc, img) => {
          acc[img.id] = {
            url: img.url,
            alt: img.alt,
            isPrimary: img.isPrimary,
            order: img.order
          };
          return acc;
        }, {} as { [key: string]: { url: string; alt: string; isPrimary: boolean; order: number } })
      };

      let savedProductId = editingProduct?.productId;
      if (editingProduct) {
        await productService.updateProduct(editingProduct.productId, productData);
      } else {
        savedProductId = await productService.createProduct(user.uid, productData);
      }

      if (
        user.email &&
        shouldNotifyLowStock(quantity, lowStockThreshold, previousQuantity)
      ) {
        try {
          await EmailService.getInstance().sendVendorLowStockNotification(
            user.email,
            user.displayName || 'Vendor',
            {
              productName: productForm.name,
              sku: productForm.sku,
              quantity,
              threshold: lowStockThreshold,
              productId: savedProductId,
            }
          );
        } catch (emailError) {
          console.warn('Low stock email failed (product was saved):', emailError);
        }
      }
      
      // Reload products
      const productsData = await productService.getVendorProducts(user.uid);
      setProducts(productsData);
      
      // Reset form
      setProductForm({
        name: '',
        description: '',
        shortDescription: '',
        currentPrice: '',
        originalPrice: '',
        currency: 'EUR',
        productType: 'physical',
        quantity: '',
        sku: '',
        lowStockThreshold: '',
        weight: '',
        length: '',
        width: '',
        height: '',
        shippingClass: 'standard',
        freeShipping: false,
        shippingCost: '',
        tags: '',
        keywords: '',
        categoryId: '',
        specifications: {}
      });
      
      setUploadedImages([]);
      setUploadProductId(null);
      setShowProductForm(false);
      setEditingProduct(null);
    } catch (err) {
      console.error('Error saving product:', err);
      const message = err instanceof Error ? err.message : 'Failed to save product';
      setError(message);
    }
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setUploadProductId(product.productId);
    setUploadedImages(
      Object.entries(product.images || {}).map(([id, img]) => ({
        id,
        url: img.url,
        path: '',
        alt: img.alt,
        isPrimary: img.isPrimary,
        order: img.order,
        size: 0,
        type: 'image/jpeg',
      }))
    );
    setProductForm({
      name: product.name,
      description: product.description,
      shortDescription: product.shortDescription,
      currentPrice: product.price.current.toString(),
      originalPrice: product.price.original.toString(),
      currency: product.price.currency,
      productType: (product.specifications?.productType as ProductType) || 'physical',
      quantity: product.inventory.quantity.toString(),
      sku: product.inventory.sku,
      lowStockThreshold: product.inventory.lowStockThreshold.toString(),
      weight: product.shipping.weight.toString(),
      length: product.shipping.dimensions.length.toString(),
      width: product.shipping.dimensions.width.toString(),
      height: product.shipping.dimensions.height.toString(),
      shippingClass: product.shipping.shippingClass,
      freeShipping: product.shipping.freeShipping,
      shippingCost: product.shipping.shippingCost.toString(),
      tags: product.tags.join(', '),
      keywords: product.keywords.join(', '),
      categoryId: product.categoryId,
      specifications: product.specifications
    });
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }
    
    try {
      await productService.deleteProduct(productId);
      
      // Reload products
      if (user) {
        const productsData = await productService.getVendorProducts(user.uid);
        setProducts(productsData);
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Failed to delete product');
    }
  };

  const handleToggleProductStatus = async (productId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'draft' ? 'active' : 'draft';
      await productService.updateProductStatus(productId, newStatus as 'draft' | 'active' | 'inactive' | 'archived');
      
      // Reload products
      if (user) {
        const productsData = await productService.getVendorProducts(user.uid);
        setProducts(productsData);
      }
    } catch (err) {
      console.error('Error updating product status:', err);
      setError('Failed to update product status');
    }
  };

  const handleImageUpload = async (files: FileList) => {
    if (!user) return;
    
    try {
      setUploadingImages(true);
      setImageUploadError(null);
      const fileArray = Array.from(files);
      
      for (const file of fileArray) {
        if (!imageUploadService.isImageFile(file)) {
          throw new Error(`${file.name} is not a valid image file`);
        }
      }
      
      const productId =
        uploadProductId || editingProduct?.productId || `temp_${user.uid}_${Date.now()}`;
      if (!uploadProductId) {
        setUploadProductId(productId);
      }
      
      const newImages = await imageUploadService.uploadProductImages(fileArray, productId);
      setUploadedImages(prev => [...prev, ...newImages]);
      
    } catch (err) {
      console.error('Error uploading images:', err);
      setImageUploadError(err instanceof Error ? err.message : 'Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleImageDelete = async (imageId: string) => {
    try {
      const imageToDelete = uploadedImages.find(img => img.id === imageId);
      const productId = uploadProductId || editingProduct?.productId;
      if (imageToDelete && productId) {
        if (imageToDelete.path) {
          await imageUploadService.deleteImage(imageToDelete.path, productId);
        }
        setUploadedImages(prev => prev.filter(img => img.id !== imageId));
      }
    } catch (err) {
      console.error('Error deleting image:', err);
      setImageUploadError(err instanceof Error ? err.message : 'Failed to delete image');
    }
  };

  const handleSetPrimaryImage = (imageId: string) => {
    setUploadedImages(prev => 
      prev.map(img => ({
        ...img,
        isPrimary: img.id === imageId
      }))
    );
  };

  // Show access denied message for non-vendor users
  if (!user || userAccountType !== 'vendor') {
    return (
      <div className="vendor-panel">
        <Navigation />
        <main className="vendor-panel-content">
          <div className="vendor-panel-container">
            <div className="access-denied">
              <ExclamationTriangleIcon className="error-icon" />
              <h1 className="error-title">Access Denied</h1>
              <p className="error-message">Vendor privileges are required to access this page.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="vendor-panel">
        <Navigation />
        <main className="vendor-panel-content">
          <div className="vendor-panel-container">
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p className="loading-message">Loading vendor data...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="vendor-panel">
        <Navigation />
        <main className="vendor-panel-content">
          <div className="vendor-panel-container">
            <div className="error-state">
              <ExclamationTriangleIcon className="error-icon" />
              <h1 className="error-title">Error</h1>
              <p className="error-message">{error}</p>
              <button 
                className="retry-btn"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const showStripeConnectLoader = stripeConnectLoading;

  return (
    <div className="vendor-panel">
      <Navigation />
      <main className="vendor-panel-content">
        <div className="vendor-panel-container">
          <div className="vendor-header">
            <h1 className="vendor-panel-title">Vendor Panel</h1>
            <p className="vendor-panel-subtitle">Manage your business on Worldasia</p>
          </div>

          {/* Navigation Tabs */}
          <div className="vendor-tabs">
            <button 
              className={`vendor-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              Dashboard
            </button>
            <button 
              className={`vendor-tab ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => setActiveTab('products')}
            >
              Products
            </button>
            <button 
              className={`vendor-tab ${activeTab === 'add-product' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('add-product');
                setShowProductForm(true);
                setEditingProduct(null);
              }}
            >
              <PlusIcon className="tab-icon" />
              Add Product
            </button>
            <button 
              className={`vendor-tab ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              Orders
            </button>
            <button
              className={`vendor-tab ${activeTab === 'support' ? 'active' : ''}`}
              onClick={() => setActiveTab('support')}
            >
              <LifebuoyIcon className="tab-icon" />
              Support
            </button>
          </div>

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="dashboard-content">
              {showStripeConnectLoader ? (
                <div className="stripe-connect-loading" style={{ marginBottom: '1rem' }}>
                  <div className="loading-spinner" />
                  <p className="loading-message">Checking Stripe connection…</p>
                </div>
              ) : !stripeConnectStatus.onboardingComplete ? (
                <div className="error-state" style={{ marginBottom: '1rem' }}>
                  <h2 className="error-title">Stripe Connect setup required</h2>
                  <p className="error-message">
                    Connect your Stripe account to receive payouts from customer orders. Buyers cannot
                    checkout with your products until onboarding is complete.
                  </p>
                  {stripeConnectStatus.connected && (
                    <p className="error-message" style={{ marginTop: '0.75rem' }}>
                      A Stripe account is already linked to this vendor profile
                      {stripeConnectStatus.accountId ? ` (${stripeConnectStatus.accountId})` : ''}.
                      If you see the wrong business in Stripe, use Start over below before connecting again.
                    </p>
                  )}
                  <div className="form-group" style={{ maxWidth: '320px', marginTop: '1rem' }}>
                    <label htmlFor="stripeConnectCountry" className="form-label">
                      Business country *
                    </label>
                    <select
                      id="stripeConnectCountry"
                      className="form-select"
                      value={stripeConnectCountry}
                      onChange={(e) => {
                        setStripeConnectCountry(e.target.value as StripeConnectCountryCode | '');
                        setStripeSetupError(null);
                      }}
                    >
                      <option value="">Select country</option>
                      {STRIPE_CONNECT_COUNTRIES.map(({ code, label }) => (
                        <option key={code} value={code}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <p className="error-message" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                      Choose where your business is registered. Stripe onboarding and payouts use this country.
                    </p>
                  </div>
                  {stripeSetupError && (
                    <p className="error-message" style={{ marginTop: '0.75rem' }}>
                      {stripeSetupError}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                    <button
                      className="retry-btn"
                      onClick={handleStripeConnectSetup}
                      disabled={!stripeConnectCountry || stripeResetting}
                    >
                      Connect Stripe
                    </button>
                    {stripeConnectStatus.connected && (
                      <button
                        type="button"
                        className="retry-btn"
                        onClick={handleStripeConnectReset}
                        disabled={stripeResetting}
                        style={{ background: 'transparent', color: '#374151', border: '1px solid #D1D5DB' }}
                      >
                        {stripeResetting ? 'Resetting…' : 'Start over'}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="error-state" style={{ marginBottom: '1rem', borderColor: '#22c55e' }}>
                  <h2 className="error-title">Stripe payouts active</h2>
                  <p className="error-message">
                    {stripeConnectStatus.chargesEnabled && stripeConnectStatus.payoutsEnabled
                      ? 'Your account can accept payments and receive payouts.'
                      : 'Finish any remaining steps in Stripe to enable charges and payouts.'}
                  </p>
                  <button className="retry-btn" onClick={handleStripeDashboard}>
                    Open Stripe Dashboard
                  </button>
                </div>
              )}
              <div className="stats-overview">
                <div className="stat-card">
                  <div className="stat-icon products">
                    <TagIcon className="stat-svg-icon" />
                  </div>
                  <div className="stat-content">
                    <h3 className="stat-number">{products.length}</h3>
                    <p className="stat-label">Total Products</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon active">
                    <CheckCircleIcon className="stat-svg-icon" />
                  </div>
                  <div className="stat-content">
                    <h3 className="stat-number">{products.filter(p => p.status === 'active').length}</h3>
                    <p className="stat-label">Active Products</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon draft">
                    <PencilIcon className="stat-svg-icon" />
                  </div>
                  <div className="stat-content">
                    <h3 className="stat-number">{products.filter(p => p.status === 'draft').length}</h3>
                    <p className="stat-label">Draft Products</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon orders">
                    <TruckIcon className="stat-svg-icon" />
                  </div>
                  <div className="stat-content">
                    <h3 className="stat-number">{vendorEarnings.totalOrders}</h3>
                    <p className="stat-label">Total Orders</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon earnings">
                    <CurrencyDollarIcon className="stat-svg-icon" />
                  </div>
                  <div className="stat-content">
                    <h3 className="stat-number">£{vendorEarnings.totalEarnings.toFixed(2)}</h3>
                    <p className="stat-label">Total Earnings</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon pending">
                    <ClockIcon className="stat-svg-icon" />
                  </div>
                  <div className="stat-content">
                    <h3 className="stat-number">£{vendorEarnings.pendingPayouts.toFixed(2)}</h3>
                    <p className="stat-label">Pending Payouts</p>
                  </div>
                </div>
              </div>

              <div className="recent-products">
                <h2 className="section-title">Recent Products</h2>
                {products.length === 0 ? (
                  <div className="no-products">
                    <TagIcon className="no-products-icon" />
                    <h3 className="no-products-title">No products yet</h3>
                    <p className="no-products-message">Start by adding your first product to the marketplace.</p>
                    <button 
                      className="add-first-product-btn"
                      onClick={() => {
                        setActiveTab('add-product');
                        setShowProductForm(true);
                      }}
                    >
                      <PlusIcon className="btn-icon" />
                      Add Your First Product
                    </button>
                  </div>
                ) : (
                  <div className="products-grid">
                    {products.slice(0, 6).map((product) => (
                      <div key={product.productId} className="product-card">
                        <div className="product-image">
                          {Object.keys(product.images).length > 0 ? (
                            <img 
                              src={Object.values(product.images)[0].url} 
                              alt={product.name}
                              className="product-img"
                            />
                          ) : (
                            <div className="no-image">
                              <PhotoIcon className="no-image-icon" />
                            </div>
                          )}
                        </div>
                        <div className="product-info">
                          <h3 className="product-name">{product.name}</h3>
                          <p className="product-price">
                            {product.price.currency} {product.price.current}
                          </p>
                          <div className="product-status">
                            <span className={`status-badge ${product.status}`}>
                              {product.status}
                            </span>
                          </div>
                        </div>
                        <div className="product-actions">
                          <button 
                            className="view-btn"
                            onClick={() => handleEditProduct(product)}
                          >
                            <EyeIcon className="action-icon" />
                          </button>
                          <button 
                            className="edit-btn"
                            onClick={() => handleEditProduct(product)}
                          >
                            <PencilIcon className="action-icon" />
                          </button>
                          <button 
                            className={`status-btn ${product.status === 'active' ? 'unpublish-btn' : 'publish-btn'}`}
                            onClick={() => handleToggleProductStatus(product.productId, product.status)}
                            title={product.status === 'active' ? 'Unpublish product' : 'Publish product'}
                          >
                            {product.status === 'active' ? 'Unpublish' : 'Publish'}
                          </button>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDeleteProduct(product.productId)}
                          >
                            <TrashIcon className="action-icon" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="products-content">
              <div className="products-header">
                <h2 className="section-title">All Products</h2>
                <button 
                  className="add-product-btn"
                  onClick={() => {
                    setShowProductForm(true);
                    setEditingProduct(null);
                  }}
                >
                  <PlusIcon className="btn-icon" />
                  Add Product
                </button>
              </div>

              {products.length === 0 ? (
                <div className="no-products">
                  <TagIcon className="no-products-icon" />
                  <h3 className="no-products-title">No products found</h3>
                  <p className="no-products-message">Start by adding your first product to the marketplace.</p>
                  <button 
                    className="add-first-product-btn"
                    onClick={() => setShowProductForm(true)}
                  >
                    <PlusIcon className="btn-icon" />
                    Add Your First Product
                  </button>
                </div>
              ) : (
                <div className="products-table">
                  <div className="table-header">
                    <div className="table-cell">Product</div>
                    <div className="table-cell">Price</div>
                    <div className="table-cell">Stock</div>
                    <div className="table-cell">Status</div>
                    <div className="table-cell">Actions</div>
                  </div>
                  {products.map((product) => (
                    <div key={product.productId} className="table-row">
                      <div className="table-cell product-cell">
                        <div className="product-image-small">
                          {Object.keys(product.images).length > 0 ? (
                            <img 
                              src={Object.values(product.images)[0].url} 
                              alt={product.name}
                              className="product-img-small"
                            />
                          ) : (
                            <div className="no-image-small">
                              <PhotoIcon className="no-image-icon-small" />
                            </div>
                          )}
                        </div>
                        <div className="product-details">
                          <h4 className="product-name">{product.name}</h4>
                          <p className="product-sku">SKU: {product.inventory.sku}</p>
                        </div>
                      </div>
                      <div className="table-cell">
                        <span className="product-price">
                          {product.price.currency} {product.price.current}
                        </span>
                      </div>
                      <div className="table-cell">
                        <span className={`stock-status ${product.inventory.isInStock ? 'in-stock' : 'out-of-stock'}`}>
                          {product.inventory.quantity} units
                        </span>
                      </div>
                      <div className="table-cell">
                        <span className={`status-badge ${product.status}`}>
                          {product.status}
                        </span>
                      </div>
                      <div className="table-cell">
                        <div className="action-buttons">
                          <button
                            className={`action-btn ${product.status === 'active' ? 'unpublish' : 'publish'}`}
                            onClick={() => handleToggleProductStatus(product.productId, product.status)}
                            title={product.status === 'active' ? 'Unpublish product' : 'Publish product'}
                          >
                            {product.status === 'active' ? 'Unpublish' : 'Publish'}
                          </button>
                          <button 
                            className="action-btn view"
                            onClick={() => handleEditProduct(product)}
                            title="View product"
                          >
                            <EyeIcon className="action-icon" />
                          </button>
                          <button 
                            className="action-btn edit"
                            onClick={() => handleEditProduct(product)}
                            title="Edit product"
                          >
                            <PencilIcon className="action-icon" />
                          </button>
                          <button 
                            className="action-btn delete"
                            onClick={() => handleDeleteProduct(product.productId)}
                            title="Delete product"
                          >
                            <TrashIcon className="action-icon" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Add/Edit Product Form */}
          {showProductForm && (
            <div className="modal-overlay" onClick={() => setShowProductForm(false)}>
              <div className="product-form-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2 className="modal-title">
                    {editingProduct ? 'Edit Product' : 'Add New Product'}
                  </h2>
                  <button 
                    className="close-modal-btn"
                    onClick={() => setShowProductForm(false)}
                  >
                    ×
                  </button>
                </div>
                
                <form className="product-form" onSubmit={handleProductSubmit}>
                  <div className="form-section">
                    <h3 className="section-title">Basic Information</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name" className="form-label">Product Name *</label>
                        <input
                          type="text"
                          id="name"
                          className="form-input"
                          value={productForm.name}
                          onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="sku" className="form-label">SKU *</label>
                        <input
                          type="text"
                          id="sku"
                          className="form-input"
                          value={productForm.sku}
                          onChange={(e) => setProductForm({...productForm, sku: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="shortDescription" className="form-label">Short Description *</label>
                      <input
                        type="text"
                        id="shortDescription"
                        className="form-input"
                        value={productForm.shortDescription}
                        onChange={(e) => setProductForm({...productForm, shortDescription: e.target.value})}
                        placeholder="Brief description for product cards"
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="description" className="form-label">Full Description *</label>
                      <textarea
                        id="description"
                        className="form-textarea"
                        value={productForm.description}
                        onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                        rows={4}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-section">
                    <h3 className="section-title">Pricing & Inventory</h3>
                    <div className="form-group">
                      <label htmlFor="productType" className="form-label">Product Type *</label>
                      <select
                        id="productType"
                        className="form-select"
                        value={productForm.productType}
                        onChange={(e) => setProductForm({
                          ...productForm,
                          productType: e.target.value as ProductType,
                        })}
                        required
                      >
                        {PRODUCT_TYPES.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.label} — {type.description}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="currentPrice" className="form-label">Current Price *</label>
                        <input
                          type="number"
                          id="currentPrice"
                          className="form-input"
                          value={productForm.currentPrice}
                          onChange={(e) => setProductForm({...productForm, currentPrice: e.target.value})}
                          step="0.01"
                          min="0"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="originalPrice" className="form-label">Original Price</label>
                        <input
                          type="number"
                          id="originalPrice"
                          className="form-input"
                          value={productForm.originalPrice}
                          onChange={(e) => setProductForm({...productForm, originalPrice: e.target.value})}
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="currency" className="form-label">Currency *</label>
                        <select
                          id="currency"
                          className="form-select"
                          value={productForm.currency}
                          onChange={(e) => setProductForm({...productForm, currency: e.target.value})}
                          required
                        >
                          <option value="EUR">EUR (€)</option>
                          <option value="USD">USD ($)</option>
                          <option value="JPY">JPY (¥)</option>
                          <option value="CNY">CNY (¥)</option>
                          <option value="KRW">KRW (₩)</option>
                          <option value="THB">THB (฿)</option>
                          <option value="VND">VND (₫)</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="quantity" className="form-label">Stock Quantity *</label>
                        <input
                          type="number"
                          id="quantity"
                          className="form-input"
                          value={productForm.quantity}
                          onChange={(e) => setProductForm({...productForm, quantity: e.target.value})}
                          min="0"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="lowStockThreshold" className="form-label">Low Stock Alert</label>
                        <input
                          type="number"
                          id="lowStockThreshold"
                          className="form-input"
                          value={productForm.lowStockThreshold}
                          onChange={(e) => setProductForm({...productForm, lowStockThreshold: e.target.value})}
                          min="0"
                          placeholder="Email alert when stock hits this level"
                        />
                      </div>
                    </div>
                  </div>

                  {productForm.productType === 'physical' && (
                  <div className="form-section">
                    <h3 className="section-title">Shipping Information</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="weight" className="form-label">Weight (grams) *</label>
                        <input
                          type="number"
                          id="weight"
                          className="form-input"
                          value={productForm.weight}
                          onChange={(e) => setProductForm({...productForm, weight: e.target.value})}
                          min="0"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="shippingClass" className="form-label">Shipping Class</label>
                        <select
                          id="shippingClass"
                          className="form-select"
                          value={productForm.shippingClass}
                          onChange={(e) => setProductForm({...productForm, shippingClass: e.target.value as 'standard' | 'express' | 'overnight'})}
                        >
                          <option value="standard">Standard</option>
                          <option value="express">Express</option>
                          <option value="overnight">Overnight</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="length" className="form-label">Length (cm)</label>
                        <input
                          type="number"
                          id="length"
                          className="form-input"
                          value={productForm.length}
                          onChange={(e) => setProductForm({...productForm, length: e.target.value})}
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="width" className="form-label">Width (cm)</label>
                        <input
                          type="number"
                          id="width"
                          className="form-input"
                          value={productForm.width}
                          onChange={(e) => setProductForm({...productForm, width: e.target.value})}
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="height" className="form-label">Height (cm)</label>
                        <input
                          type="number"
                          id="height"
                          className="form-input"
                          value={productForm.height}
                          onChange={(e) => setProductForm({...productForm, height: e.target.value})}
                          min="0"
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group checkbox-group">
                        <input
                          type="checkbox"
                          id="freeShipping"
                          className="form-checkbox"
                          checked={productForm.freeShipping}
                          onChange={(e) => setProductForm({...productForm, freeShipping: e.target.checked})}
                        />
                        <label htmlFor="freeShipping" className="checkbox-label">Free Shipping</label>
                      </div>
                      {!productForm.freeShipping && (
                        <div className="form-group">
                          <label htmlFor="shippingCost" className="form-label">Shipping Cost</label>
                          <input
                            type="number"
                            id="shippingCost"
                            className="form-input"
                            value={productForm.shippingCost}
                            onChange={(e) => setProductForm({...productForm, shippingCost: e.target.value})}
                            step="0.01"
                            min="0"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  )}

                  <div className="form-section">
                    <h3 className="section-title">SEO & Discovery</h3>
                    <div className="form-group">
                      <label htmlFor="categoryId" className="form-label">Category *</label>
                      <select
                        id="categoryId"
                        className="form-select"
                        value={productForm.categoryId}
                        onChange={(e) => setProductForm({...productForm, categoryId: e.target.value})}
                        required
                      >
                        <option value="">Select a category</option>
                        {PRODUCT_CATEGORIES.map((category) => (
                          <option key={category.id} value={category.id}>
                            {getCategoryFormLabel(category)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="tags" className="form-label">Tags</label>
                      <input
                        type="text"
                        id="tags"
                        className="form-input"
                        value={productForm.tags}
                        onChange={(e) => setProductForm({...productForm, tags: e.target.value})}
                        placeholder="Comma-separated tags (e.g., organic, handmade, premium)"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="keywords" className="form-label">SEO Keywords</label>
                      <input
                        type="text"
                        id="keywords"
                        className="form-input"
                        value={productForm.keywords}
                        onChange={(e) => setProductForm({...productForm, keywords: e.target.value})}
                        placeholder="Comma-separated keywords for search optimization"
                      />
                    </div>
                  </div>

                  <div className="form-section">
                    <h3 className="section-title">Images</h3>
                    <div className="image-upload-area">
                      <input
                        type="file"
                        id="image-upload"
                        multiple
                        accept="image/*"
                        onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="image-upload" className="upload-label">
                        <PhotoIcon className="upload-icon" />
                        <p className="upload-text">
                          {uploadingImages ? 'Uploading images...' : 'Click to upload product images'}
                        </p>
                        <p className="upload-hint">Drag and drop images here or click to browse (JPEG, PNG, WebP, max 4MB)</p>
                      </label>
                    </div>
                    {imageUploadError && (
                      <p className="error-message" style={{ marginTop: '0.75rem' }}>
                        {imageUploadError}
                      </p>
                    )}
                    
                    {uploadedImages.length > 0 && (
                      <div className="uploaded-images">
                        <h4 className="images-title">Uploaded Images</h4>
                        <div className="images-grid">
                          {uploadedImages.map((image, index) => (
                            <div key={image.id} className="image-preview">
                              <img src={image.url} alt={image.alt} className="preview-img" />
                              <div className="image-actions">
                                <button
                                  type="button"
                                  className={`primary-btn ${image.isPrimary ? 'active' : ''}`}
                                  onClick={() => handleSetPrimaryImage(image.id)}
                                >
                                  {image.isPrimary ? 'Primary' : 'Set Primary'}
                                </button>
                                <button
                                  type="button"
                                  className="delete-btn"
                                  onClick={() => handleImageDelete(image.id)}
                                >
                                  <TrashIcon className="action-icon" />
                                </button>
                              </div>
                              {image.isPrimary && (
                                <div className="primary-badge">Primary</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={() => setShowProductForm(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="save-btn"
                    >
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="orders-content">
              <h2 className="section-title">Orders</h2>
              {orders.length === 0 ? (
                <div className="no-orders">
                  <TruckIcon className="no-orders-icon" />
                  <h3 className="no-orders-title">No orders yet</h3>
                  <p className="no-orders-message">Orders will appear here once customers start purchasing your products.</p>
                </div>
              ) : (
                <div className="orders-list">
                  {orders.map((order) => {
                    const vendorOrder = order.vendorOrders.find(vo => vo.vendorId === user?.uid);
                    if (!vendorOrder) return null;
                    
                    return (
                      <div key={order.id} className="order-card">
                        <div className="order-header">
                          <div className="order-info">
                            <h3 className="order-id">Order #{order.id.slice(-8)}</h3>
                            <p className="order-customer">Customer: {order.customerName}</p>
                            <p className="order-date">
                              {order.createdAt.toLocaleDateString()} at {order.createdAt.toLocaleTimeString()}
                            </p>
                          </div>
                          <div className="order-status">
                            <span className={`status-badge ${vendorOrder.status}`}>
                              {vendorOrder.status}
                            </span>
                            <span className="order-total">£{vendorOrder.total.toFixed(2)}</span>
                          </div>
                        </div>
                        
                        <div className="order-items">
                          <h4 className="items-title">Items:</h4>
                          {vendorOrder.items.map((item, index) => (
                            <div key={index} className="order-item">
                              <div className="item-info">
                                <span className="item-name">{item.name}</span>
                                <span className="item-quantity">x{item.quantity}</span>
                              </div>
                              <span className="item-price">£{item.total.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="order-totals">
                          <div className="total-row">
                            <span>Subtotal:</span>
                            <span>£{vendorOrder.subtotal.toFixed(2)}</span>
                          </div>
                          <div className="total-row">
                            <span>Shipping:</span>
                            <span>£{vendorOrder.shippingCost.toFixed(2)}</span>
                          </div>
                          <div className="total-row">
                            <span>Platform Fee (5%):</span>
                            <span>£{vendorOrder.platformFee.toFixed(2)}</span>
                          </div>
                          <div className="total-row vendor-earnings">
                            <span>Your Earnings:</span>
                            <span>£{vendorOrder.vendorEarnings.toFixed(2)}</span>
                          </div>
                        </div>
                        
                        <div className="order-actions">
                          {vendorOrder.status === 'pending' && (
                            <button 
                              className="action-btn confirm"
                              onClick={() => {
                                // TODO: Implement order confirmation
                                console.log('Confirm order:', order.id);
                              }}
                            >
                              Confirm Order
                            </button>
                          )}
                          {vendorOrder.status === 'confirmed' && (
                            <button 
                              className="action-btn ship"
                              onClick={() => {
                                // TODO: Implement shipping
                                console.log('Mark as shipped:', order.id);
                              }}
                            >
                              Mark as Shipped
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'support' && (
            <div className="support-content">
              <h2 className="section-title">Vendor Support</h2>
              <p className="support-intro">
                Need help with your shop, orders, or payouts? Submit a ticket and our support team will get back to you.
              </p>
              <button
                type="button"
                className="add-product-btn"
                onClick={() => setShowSupportModal(true)}
              >
                <LifebuoyIcon className="btn-icon" />
                Contact Support
              </button>
            </div>
          )}
        </div>
      </main>

      <SupportTicketModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        type="vendor_support"
      />
    </div>
  );
};

export default VendorPanel;
